import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SetupPanel } from "@/components/setup-panel";
import { SampleBanner } from "@/components/sample-banner";
import { SkHeader, SkPanel } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { GoogleAdsResults } from "@/features/google-ads/results-report";
import type { GoogleAdsResultsPage } from "@shared/pages/google-ads-results";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

// The owner's first question, as its own page in the sidebar: the ads feed
// gives the spend, Shopify gives the orders, results-report.tsx draws it.

export const Route = createFileRoute("/_app/google-ads-results")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<GoogleAdsResultsPage>("google-ads-results", deps));
  },
  component: GoogleAdsResultsRoute,
});

function Skeleton() {
  return (
    <div className="page-shell">
      <SkHeader />
      <div className="mt-5 space-y-4">
        <SkPanel bodyClassName="h-44" />
        <div className="grid gap-4 @5xl:grid-cols-5">
          <SkPanel className="@5xl:col-span-2" bodyClassName="h-80" />
          <SkPanel className="@5xl:col-span-3" bodyClassName="h-80" />
        </div>
        <div className="grid gap-4 @3xl:grid-cols-2">
          <SkPanel bodyClassName="h-32" />
          <SkPanel bodyClassName="h-32" />
        </div>
      </div>
    </div>
  );
}

function GoogleAdsResultsRoute() {
  useTitle("Google Ads results");
  const search = Route.useSearch();
  return (
    <PageQuery<GoogleAdsResultsPage> name="google-ads-results" search={search} skeleton={<Skeleton />}>
      {(page) => <GoogleAdsResultsPageView page={page} />}
    </PageQuery>
  );
}

function GoogleAdsResultsPageView({ page }: { page: GoogleAdsResultsPage }) {
  const { range: r, report } = page;
  const header = (
    <PageHeader
      title="Google Ads results"
      info="Shopify's own orders split by whether the shopper came through a Google ad, against what the ads cost."
      actions={
        page.canPickRange ? (
          <DateRangeFilter presets={r.presets} defaultKey="last_month" />
        ) : (
          <span className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm">
            <span className="font-semibold text-foreground">{r.title}</span>
            {r.range.span && <span className="text-muted">{r.range.span}</span>}
          </span>
        )
      }
    />
  );

  if (!report) {
    return (
      <div className="page-shell">
        {header}
        {page.source === "unavailable" ? (
          <SetupPanel title="Google Ads numbers aren't available right now">
            Google Ads numbers didn&apos;t come through for this range. Try again in a few minutes; if it keeps happening, ask
            Digital King to check the connection.
          </SetupPanel>
        ) : (
          <SetupPanel title="Google Ads isn't connected yet">The answer shows here once Digital King connects the Google Ads account.</SetupPanel>
        )}
      </div>
    );
  }

  return (
    <div className="page-shell">
      {header}
      {page.source === "sample" && <SampleBanner what="Google Ads numbers" source="Google Ads" />}
      <GoogleAdsResults page={page} report={report} />
    </div>
  );
}
